/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { apiClient } from '@/lib/apiClient';

/**
 * Usage-analytics tracking. Mirrors the Angular `MetricsService`:
 * - On login, POST `{ routes, loginDateTime }` to create (or reuse) the day's row and cache its id.
 * - On every route change, PUT the current full URL so the backend folds it into that day's routes.
 *
 * The id/day/payload are cached in `localStorage` so a same-day refresh reuses the existing row
 * (one row per user per UTC calendar day) instead of creating a duplicate.
 */

const STORAGE_ID_KEY = 'metricsCurrentId';
const STORAGE_DATE_KEY = 'metricsCurrentDate';
const STORAGE_PAYLOAD_KEY = 'metricsStartPayload';
const STORAGE_USER_KEY = 'metricsCurrentUser';

interface StartPayload {
  routes: string[];
  loginDateTime: string | null;
}

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
};

const remove = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
};

/** UTC calendar day (YYYY-MM-DD) for the given login timestamp, or today when absent. */
function dayOf(loginDateTime: string | null): string {
  const d = loginDateTime ? new Date(loginDateTime) : new Date();
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const date = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${date}`;
}

function loadPayload(): StartPayload | null {
  const raw = read(STORAGE_PAYLOAD_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StartPayload;
  } catch {
    remove(STORAGE_PAYLOAD_KEY);
    return null;
  }
}

/**
 * Records the login row for the day and caches its id. Reuses the cached id when it belongs to the
 * current UTC day so same-day refreshes skip the POST. Returns the row id (0 when unauthenticated).
 */
export async function startMetrics(
  userId: string,
  routes: string[],
  loginDateTime: string | null,
): Promise<number> {
  if (!userId) return 0;
  const day = dayOf(loginDateTime);

  // Persist the payload up front so updateMetrics always has something to send, even when the
  // cached id short-circuits the POST below (a stale/evicted payload would otherwise no-op forever).
  const payload: StartPayload = { routes: [...routes], loginDateTime };
  write(STORAGE_PAYLOAD_KEY, JSON.stringify(payload));

  // Reuse the cached row only when it is a valid numeric id from the same user on the same UTC day.
  // Keying on the user avoids reusing another account's row on a shared machine.
  const cachedId = read(STORAGE_ID_KEY);
  if (
    cachedId &&
    /^\d+$/.test(cachedId) &&
    read(STORAGE_DATE_KEY) === day &&
    read(STORAGE_USER_KEY) === userId
  ) {
    return Number(cachedId);
  }

  const { data } = await apiClient.post<number>(
    `/ediagnostics/v1/user/metrics/${encodeURIComponent(userId)}`,
    payload,
  );
  write(STORAGE_ID_KEY, String(data));
  write(STORAGE_DATE_KEY, day);
  write(STORAGE_USER_KEY, userId);
  return data;
}

/**
 * Appends the current full route to the day's row. No-op until {@link startMetrics} has run
 * (there is no id/payload to update yet).
 */
export async function updateMetrics(userId: string, fullRoute?: string): Promise<void> {
  if (!userId) return;
  const id = read(STORAGE_ID_KEY);
  const payload = loadPayload();
  // Only update when the cached row belongs to this user (ignore another account's leftover cache).
  if (!id || read(STORAGE_USER_KEY) !== userId || !payload) return;

  if (fullRoute) {
    payload.routes = [fullRoute];
    write(STORAGE_PAYLOAD_KEY, JSON.stringify(payload));
  }
  await apiClient.put<number>(
    `/ediagnostics/v1/user/metrics/${encodeURIComponent(userId)}`,
    payload,
    { params: { id } },
  );
}

/** Clears the cached id/day/payload (e.g. on logout) so the next login POSTs a fresh row. */
export function clearMetrics(): void {
  remove(STORAGE_ID_KEY);
  remove(STORAGE_DATE_KEY);
  remove(STORAGE_PAYLOAD_KEY);
  remove(STORAGE_USER_KEY);
}
